import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppConfig } from "@/hooks/useAppConfig";
import StreamText from "./effects/StreamText";
import NumberTicker from "./effects/NumberTicker";
import MagnetButton from "./effects/MagnetButton";
import { HERO_STATS } from "@/lib/creator";
import GalleryFan from "./effects/GalleryFan";

const FALLBACK_PHOTOS = ["/photos/01.png", "/photos/02.png", "/photos/03.png"];

interface HeroSectionProps {
  t: Record<string, string>;
  active: boolean;
  onCTA: () => void;
  onSkipTour?: () => void;
}

const ease = [0.2, 0.8, 0.2, 1] as const;

const fadeSlideUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.7, ease },
  }),
  exit: { opacity: 0, y: -12, transition: { duration: 0.3 } },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: {
    opacity: 1, scale: 1,
    transition: { duration: 0.6, ease },
  },
};

const statSlideUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: 0.5 + i * 0.12, duration: 0.6, ease },
  }),
};

export const HeroSection: React.FC<HeroSectionProps> = ({ t, active, onCTA, onSkipTour }) => {
  const { config } = useAppConfig();
  const name = config.profile.name || "Creator";
  const nameParts = name.split(" ");
  const firstName = nameParts[0] || "Creator";
  const lastName = nameParts.slice(1).join(" ") || "";
  const bio = config.profile.bio || "Todo lo mío en un solo lugar — canales privados, redes, contacto directo. Sin correos. Sin vueltas.";
  const tickerKeyRef = useRef(0);
  const photos = config.photos && config.photos.length ? config.photos : FALLBACK_PHOTOS;
  const freeChannel = config.channel?.freeChannelInviteLink || '';
  const openFreeChannel = () => {
    const tg = window.Telegram?.WebApp;
    if (tg && /^https:\/\/t\.me\//.test(freeChannel)) tg.openTelegramLink(freeChannel);
    else window.open(freeChannel, '_blank', 'noopener');
  };

  // Increment key each time we become active to re-trigger tickers
  if (active) tickerKeyRef.current++;

  return (
    <div className="absolute inset-0 font-body" style={{ color: 'var(--c-text)', fontFamily: 'var(--ff-body)' }}>
      <AnimatePresence mode="wait">
        {active && (
          <motion.div
            key="hero-content"
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute inset-0"
          >
            {/* divider line */}
            <motion.div
              variants={fadeSlideUp}
              custom={0}
              className="absolute left-[18px] right-[18px] h-[1px]"
              style={{ top: 'var(--safe-top, 62px)', background: 'var(--c-line-soft)' }}
            />

            {/* top meta */}
            <motion.div
              variants={fadeSlideUp}
              custom={0}
              className="absolute left-[18px] right-[18px] flex justify-between"
              style={{ top: 'calc(var(--safe-top, 62px) + 16px)' }}
            >
              <div style={{
                fontFamily: 'var(--ff-body)', fontSize: 'var(--fs-micro)',
                letterSpacing: '0.22em', textTransform: 'uppercase',
                color: 'var(--c-text-dim)', fontWeight: 600,
              }}>
                <StreamText text="Telegram · Mini App" speed={30} delay={200} caret={false} />
              </div>
              <div style={{
                fontFamily: 'var(--ff-body)', fontSize: 'var(--fs-micro)',
                letterSpacing: '0.22em', textTransform: 'uppercase',
                color: 'var(--c-text-dim)', fontWeight: 600,
              }}>v4.5</div>
            </motion.div>

            {/* Main content */}
            <div className="hero-main absolute inset-x-0 flex flex-col px-[18px]" style={{ top: 'calc(var(--safe-top, 62px) + 44px)' }}>
              {/* Abanico de fotos del creador (se abre al tocarlo) */}
              <motion.div variants={scaleIn} className="hero-fan mb-4 shrink-0">
                <GalleryFan photos={photos} name={name} />
              </motion.div>

              <motion.div variants={fadeSlideUp} custom={1} className="hero-badge mb-3">
                <div style={{
                  fontFamily: 'var(--ff-body)', fontSize: 'var(--fs-micro)',
                  letterSpacing: '0.22em', textTransform: 'uppercase',
                  color: 'var(--c-crimson)', fontWeight: 600,
                }}>{t.hero_badge || "Bienvenido"}</div>
              </motion.div>

              <motion.h1
                variants={fadeSlideUp}
                custom={2}
                style={{
                  fontFamily: 'var(--ff-display)', fontSize: 'min(var(--fs-hero), 7.2vh)',
                  lineHeight: 0.86, letterSpacing: '-0.01em', margin: 0,
                  textTransform: 'uppercase',
                }}
              >
                {firstName}<br />{lastName}
              </motion.h1>

              <motion.div
                variants={fadeSlideUp}
                custom={3}
                style={{
                  fontFamily: 'var(--ff-italic)', fontStyle: 'italic',
                  fontSize: 'clamp(24px, 4vw, 32px)', lineHeight: 1,
                  color: 'var(--c-crimson)', marginTop: 10,
                  letterSpacing: '-0.01em', minHeight: 38,
                }}
              >
                <StreamText
                  text={t.hero_title_2 || "¿dónde la quieres?"}
                  speed={48}
                  delay={650}
                />
              </motion.div>

              <motion.p
                variants={fadeSlideUp}
                custom={4}
                style={{
                  marginTop: 'var(--sp-3)', fontSize: 'var(--fs-body)',
                  lineHeight: 1.6, color: 'var(--c-text-dim)',
                  maxWidth: 280, fontWeight: 400,
                }}
              >
                {bio}
              </motion.p>



              <motion.div variants={fadeSlideUp} custom={5} className="mt-4 flex items-center gap-[14px]">
                <MagnetButton
                  onClick={onCTA}
                  className="glow-button"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 10,
                    background: 'var(--c-crimson)', color: 'var(--c-on-crimson)', border: 'none',
                    padding: '13px 22px', borderRadius: 'var(--radius-pill, 999px)', cursor: 'pointer',
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.16em',
                    textTransform: 'uppercase' as const, fontFamily: 'var(--ff-body)',
                  }}
                >
                  {t.cta_button || "Empezar"} <span style={{ fontSize: 14, lineHeight: 1 }}>→</span>
                </MagnetButton>
                <motion.a
                  onClick={onSkipTour}
                  whileHover={{ color: 'var(--c-text)' }}
                  style={{
                    fontSize: 'var(--fs-micro)', letterSpacing: '0.22em',
                    color: 'var(--c-text-dim)', textTransform: 'uppercase',
                    fontWeight: 600, cursor: 'pointer', transition: 'color 0.2s',
                  }}
                >
                  {t.hero_skip_tour || "Saltar tour"}
                </motion.a>
              </motion.div>

              {/* Canal gratis */}
              {freeChannel && (
                <motion.div variants={fadeSlideUp} custom={6} className="mt-3">
                  <button
                    type="button"
                    onClick={openFreeChannel}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      background: 'transparent', color: 'var(--c-text)',
                      border: '1px solid var(--c-line)', padding: '11px 20px',
                      borderRadius: 'var(--radius-pill, 999px)', cursor: 'pointer',
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.16em',
                      textTransform: 'uppercase', fontFamily: 'var(--ff-body)',
                    }}
                  >
                    {t.hero_free_channel || "Canal gratis"} <span aria-hidden="true">↗</span>
                  </button>
                </motion.div>
              )}
            </div>

            {/* stats strip — moved up above FAB zone (bottom: 120px) */}
            <div className="hero-stats absolute left-[18px] right-[18px]" style={{ bottom: 120 }}>
              <motion.div
                variants={scaleIn}
                style={{
                  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 0, borderTop: '1px solid var(--c-line)', paddingTop: 14,
                }}
              >
                {HERO_STATS.map((s, i) => (
                  <motion.div
                    key={s.label}
                    variants={statSlideUp}
                    custom={i}
                    style={{
                      borderRight: i < 2 ? '1px solid var(--c-line)' : 'none',
                      paddingLeft: i === 0 ? 0 : 14,
                    }}
                  >
                    <div style={{
                      fontFamily: 'var(--ff-display)', fontSize: 'var(--fs-heading)',
                      color: 'var(--c-text)', letterSpacing: '0.02em', lineHeight: 1,
                    }}>
                      <NumberTicker key={`tk-${s.label}-${tickerKeyRef.current}`} to={s.v} prefix={s.pref} suffix={s.suf} />
                    </div>
                    <div style={{
                      fontSize: 'var(--fs-micro)', letterSpacing: '0.2em',
                      color: 'var(--c-muted)', textTransform: 'uppercase', marginTop: 4,
                    }}>{s.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* footer — page meta only (removed italic overlap) */}
            <motion.div variants={fadeSlideUp} custom={6} style={{ position: 'absolute', bottom: 58, left: 18 }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 10,
                fontFamily: 'var(--ff-body)', color: 'var(--c-text-dim)',
                fontSize: 'var(--fs-caption)', letterSpacing: '0.18em', textTransform: 'uppercase',
              }}>
                <span style={{
                  fontFamily: 'var(--ff-display)', fontSize: 22,
                  color: 'var(--c-text)', letterSpacing: '0.02em', lineHeight: 1,
                }}>01</span>
                <span style={{ color: 'var(--c-muted)' }}>/ 05</span>
                <span style={{ width: 24, height: 1, background: 'var(--c-line)', margin: '0 6px', display: 'inline-block' }}></span>
                <span>{t.hero_badge || "Inicio"}</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HeroSection;
